export function DocumentAIPage() {
    return (
        <>
            {/* Hero Section */}
            <section className="hero-section">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="hero-content">
                                <h1 className="display-4 fw-bold mb-4">
                                    Intelligent Document Processing
                                </h1>
                                <p className="lead mb-4">
                                    Transform unstructured documents into actionable Salesforce data using Google Cloud Document AI.
                                    Leverage our ECM integration to automatically process uploads and create structured records.
                                </p>
                                <div className="d-flex gap-3 flex-wrap">
                                    <a className="btn btn-modern btn-lg" href="/login">
                                        Get Started
                                    </a>
                                    <a className="btn btn-outline-modern btn-lg" href="#features">
                                        Learn More
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 mt-5 mt-lg-0">
                            <div className="text-center">
                                <div className="feature-card bg-white text-dark p-4">
                                    <div className="feature-icon">
                                        <i className="bi bi-cpu"></i>
                                    </div>
                                    <h4 className="fw-semibold mb-3">AI-Powered Processing</h4>
                                    <p className="mb-0">Google Cloud Document AI extracts structured data from PDFs, images, and documents, automatically creating Salesforce records</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Overview */}
            <section id="features" className="py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center mb-5">
                            <h2 className="display-5 fw-semibold mb-3">
                                From Documents to Salesforce Records
                            </h2>
                            <p className="lead text-muted">
                                Upload documents through Salesforce UI, process them with Google Cloud Document AI,
                                and automatically create structured Salesforce records with extracted data.
                            </p>
                        </div>
                    </div>
                    <div className="row g-4">
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-file-earmark-text"></i>
                                </div>
                                <h3>Invoice Processing</h3>
                                <p>
                                    Automatically extract invoice details (vendor, amount, line items, dates) and create
                                    Invoice and Invoice Line Item records in Salesforce with structured data.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-heart-pulse"></i>
                                </div>
                                <h3>Lab Results Processing</h3>
                                <p>
                                    Process patient lab results, extract test values and findings, and create
                                    corresponding Lab Result records linked to Patient records in Salesforce.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-file-earmark-check"></i>
                                </div>
                                <h3>Contract Analysis</h3>
                                <p>
                                    Extract key contract terms, dates, parties, and obligations from legal documents
                                    to populate Contract records with structured metadata.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-receipt"></i>
                                </div>
                                <h3>Receipt Processing</h3>
                                <p>
                                    Process expense receipts to extract merchant, amount, date, and category data
                                    for automatic expense record creation and approval workflows.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-person-vcard"></i>
                                </div>
                                <h3>ID Document Verification</h3>
                                <p>
                                    Extract personal information from driver's licenses, passports, and IDs
                                    to create or update Contact and Account records with verified data.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-graph-up"></i>
                                </div>
                                <h3>Financial Statements</h3>
                                <p>
                                    Process financial statements and reports to extract key metrics and populate
                                    Financial Record objects for analysis and reporting workflows.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Case: Invoice Processing */}
            <section className="py-5 bg-light">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center mb-5">
                            <h2 className="display-5 fw-semibold mb-3">
                                Invoice Processing Use Case
                            </h2>
                            <p className="lead text-muted">
                                Transform PDF invoices into structured Salesforce Invoice and Line Item records automatically
                            </p>
                        </div>
                    </div>

                    {/* Workflow Diagram */}
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-xl-10">
                            <div className="card shadow-lg border-0 rounded-3 p-4">
                                <div className="workflow-diagram">
                                    {/* Step 1: Document Upload */}
                                    <div className="workflow-step">
                                        <div className="step-icon bg-primary">
                                            <i className="bi bi-upload text-white"></i>
                                        </div>
                                        <h5 className="fw-semibold mt-3">Upload Invoice</h5>
                                        <p className="text-muted small">Salesforce native file upload</p>
                                        <div className="step-arrow">
                                            <i className="bi bi-arrow-right text-primary"></i>
                                        </div>
                                    </div>

                                    {/* Step 2: AI Processing */}
                                    <div className="workflow-step">
                                        <div className="step-icon bg-success">
                                            <i className="bi bi-cpu text-white"></i>
                                        </div>
                                        <h5 className="fw-semibold mt-3">Document AI</h5>
                                        <p className="text-muted small">GCP extracts invoice data</p>
                                        <div className="step-arrow">
                                            <i className="bi bi-arrow-right text-success"></i>
                                        </div>
                                    </div>

                                    {/* Step 3: Data Extraction */}
                                    <div className="workflow-step">
                                        <div className="step-icon bg-warning">
                                            <i className="bi bi-table text-white"></i>
                                        </div>
                                        <h5 className="fw-semibold mt-3">Extract Data</h5>
                                        <p className="text-muted small">Vendor, amount, line items</p>
                                        <div className="step-arrow">
                                            <i className="bi bi-arrow-right text-warning"></i>
                                        </div>
                                    </div>

                                    {/* Step 4: Salesforce Records */}
                                    <div className="workflow-step">
                                        <div className="step-icon bg-info">
                                            <i className="bi bi-database text-white"></i>
                                        </div>
                                        <h5 className="fw-semibold mt-3">Create Records</h5>
                                        <p className="text-muted small">Invoice & Line Item objects</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Use Case Description */}
                    <div className="row">
                        <div className="col-lg-6 mb-4">
                            <div className="card h-100 border-0 shadow">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="feature-icon me-3" style={{width: '3rem', height: '3rem', fontSize: '1.2rem'}}>
                                            <i className="bi bi-file-earmark-text"></i>
                                        </div>
                                        <h4 className="fw-semibold mb-0">Automated Data Extraction</h4>
                                    </div>
                                    <p className="text-muted">
                                        Google Cloud Document AI automatically extracts key invoice information including
                                        vendor details, invoice number, total amount, line items, tax information, and due dates
                                        from PDF or image invoices.
                                    </p>
                                    <ul className="list-unstyled">
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Vendor name and address
                                        </li>
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Invoice number and dates
                                        </li>
                                        <li className="d-flex align-items-center">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Line items with quantities and prices
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 mb-4">
                            <div className="card h-100 border-0 shadow">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="feature-icon me-3" style={{width: '3rem', height: '3rem', fontSize: '1.2rem'}}>
                                            <i className="bi bi-database"></i>
                                        </div>
                                        <h4 className="fw-semibold mb-0">Salesforce Record Creation</h4>
                                    </div>
                                    <p className="text-muted">
                                        Extracted data automatically populates Salesforce Invoice records with header information
                                        and creates related Invoice Line Item records for each product or service. Records are
                                        linked to existing Accounts when vendors are matched.
                                    </p>
                                    <ul className="list-unstyled">
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Invoice header records
                                        </li>
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Invoice line item details
                                        </li>
                                        <li className="d-flex align-items-center">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Account matching and linking
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Extraction Examples */}
                    <div className="row mt-5">
                        <div className="col-12">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body p-4">
                                    <h4 className="fw-semibold mb-4 text-center">Extracted Data Fields</h4>
                                    <div className="row">
                                        <div className="col-md-3 text-center mb-3">
                                            <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                                <i className="bi bi-building text-primary fs-4"></i>
                                            </div>
                                            <h6 className="fw-semibold">Vendor Information</h6>
                                            <p className="small text-muted">Company name, address, tax ID, contact details</p>
                                        </div>
                                        <div className="col-md-3 text-center mb-3">
                                            <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                                <i className="bi bi-receipt text-success fs-4"></i>
                                            </div>
                                            <h6 className="fw-semibold">Invoice Details</h6>
                                            <p className="small text-muted">Invoice number, date, due date, purchase order</p>
                                        </div>
                                        <div className="col-md-3 text-center mb-3">
                                            <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                                <i className="bi bi-list-ol text-warning fs-4"></i>
                                            </div>
                                            <h6 className="fw-semibold">Line Items</h6>
                                            <p className="small text-muted">Product description, quantity, unit price, total</p>
                                        </div>
                                        <div className="col-md-3 text-center mb-3">
                                            <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                                <i className="bi bi-calculator text-info fs-4"></i>
                                            </div>
                                            <h6 className="fw-semibold">Financial Totals</h6>
                                            <p className="small text-muted">Subtotal, tax amount, discounts, final total</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Technical Capabilities */}
            { /* 
            <section className="py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center mb-5">
                            <h2 className="display-5 fw-semibold mb-3">
                                Google Cloud Document AI Integration
                            </h2>
                            <p className="lead text-muted">
                                Leverage enterprise-grade AI processing capabilities built on Google's machine learning platform
                            </p>
                        </div>
                    </div>
                    <div className="row g-4">
                        <div className="col-md-6 col-lg-3">
                            <div className="text-center">
                                <div className="display-4 text-primary fw-bold mb-2">99.9%</div>
                                <h5 className="fw-semibold">Accuracy</h5>
                                <p className="text-muted">OCR and data extraction precision</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="text-center">
                                <div className="display-4 text-success fw-bold mb-2">100+</div>
                                <h5 className="fw-semibold">Document Types</h5>
                                <p className="text-muted">Invoices, forms, IDs, receipts, etc.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="text-center">
                                <div className="display-4 text-warning fw-bold mb-2">50+</div>
                                <h5 className="fw-semibold">Languages</h5>
                                <p className="text-muted">Multi-language OCR support</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="text-center">
                                <div className="display-4 text-info fw-bold mb-2">&lt;5s</div>
                                <h5 className="fw-semibold">Processing Time</h5>
                                <p className="text-muted">Real-time document analysis</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            */}

            {/* Additional Use Cases */}
            <section className="py-5 bg-light">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center mb-5">
                            <h2 className="display-5 fw-semibold mb-3">
                                More Document Processing Use Cases
                            </h2>
                            <p className="lead text-muted">
                                Extend beyond invoices with specialized processors for various document types
                            </p>
                        </div>
                    </div>
                    <div className="row g-4">
                        <div className="col-lg-4 mb-4">
                            <div className="card h-100 border-0 shadow">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="feature-icon me-3" style={{width: '3rem', height: '3rem', fontSize: '1.2rem'}}>
                                            <i className="bi bi-heart-pulse"></i>
                                        </div>
                                        <h4 className="fw-semibold mb-0">Healthcare Documents</h4>
                                    </div>
                                    <p className="text-muted mb-3">
                                        Process lab results, medical forms, and patient documents to create
                                        structured healthcare records in Salesforce Health Cloud.
                                    </p>
                                    <ul className="list-unstyled">
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Lab result processing
                                        </li>
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Patient intake forms
                                        </li>
                                        <li className="d-flex align-items-center">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Insurance documentation
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 mb-4">
                            <div className="card h-100 border-0 shadow">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="feature-icon me-3" style={{width: '3rem', height: '3rem', fontSize: '1.2rem'}}>
                                            <i className="bi bi-briefcase"></i>
                                        </div>
                                        <h4 className="fw-semibold mb-0">Business Documents</h4>
                                    </div>
                                    <p className="text-muted mb-3">
                                        Extract data from contracts, purchase orders, and business forms
                                        to populate relevant Salesforce business objects automatically.
                                    </p>
                                    <ul className="list-unstyled">
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Contract processing
                                        </li>
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Purchase order automation
                                        </li>
                                        <li className="d-flex align-items-center">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Expense report processing
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 mb-4">
                            <div className="card h-100 border-0 shadow">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="feature-icon me-3" style={{width: '3rem', height: '3rem', fontSize: '1.2rem'}}>
                                            <i className="bi bi-person-vcard"></i>
                                        </div>
                                        <h4 className="fw-semibold mb-0">Identity Documents</h4>
                                    </div>
                                    <p className="text-muted mb-3">
                                        Process IDs, licenses, and personal documents to verify customer
                                        information and create or update Contact and Account records.
                                    </p>
                                    <ul className="list-unstyled">
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Driver's license processing
                                        </li>
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Passport verification
                                        </li>
                                        <li className="d-flex align-items-center">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Customer onboarding
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center">
                            <h2 className="fw-semibold mb-3">Transform Documents into Structured Salesforce Data</h2>
                            <p className="lead text-muted mb-4">
                                Leverage Google Cloud Document AI with our ECM integration to automatically process uploads and create structured Salesforce records.
                            </p>
                            <div className="d-flex gap-3 justify-content-center flex-wrap">
                                <a className="btn btn-modern btn-lg" href="/login">
                                    Start Processing Documents
                                </a>
                                <a
                                    className="btn btn-outline-modern btn-lg"
                                    href="https://www.arcanescale.com/contact-us"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Contact Sales
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}